import { OrganizationSelections } from '@socialgoodsoftware/libraries/services/organization-selection';

import type { Organization } from './types';
import type { OrganizationSelection } from './types';

const cacheKeyPrefix = 'socialgood:organizations:';
const selectedCacheKeyPrefix = 'socialgood:organization:';

function BuildOrganizationMembershipCacheKey(userKey: string): string {
  let key = '';
  if (typeof userKey === 'string' && userKey.trim().length > 0) {
    key = `${cacheKeyPrefix}${userKey.trim().toLowerCase()}`;
  }
  return key;
}

export function ReadOrganizationMembershipCache(userKey: string): Organization[] | null {
  let list: Organization[] | null = null;
  const storageKey = BuildOrganizationMembershipCacheKey(userKey);
  if (storageKey.length > 0 && typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
          const organizations: Organization[] = [];
          for (let index = 0; index < parsed.length; index = index + 1) {
            const entry = OrganizationSelections.From(parsed[index]);
            if (entry) {
              organizations.push(entry);
            }
          }
          if (organizations.length > 0) {
            list = organizations;
          }
        }
      }
    } catch {
      list = null;
    }
  }
  return list;
}

export function WriteOrganizationMembershipCache(userKey: string, organizations: Organization[]): void {
  const storageKey = BuildOrganizationMembershipCacheKey(userKey);
  if (storageKey.length > 0 && typeof window !== 'undefined' && window.localStorage && Array.isArray(organizations)) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(organizations));
    } catch {
      /* quota / private mode — ignore */
    }
  }
}

export function ClearOrganizationMembershipCache(userKey: string): void {
  const storageKey = BuildOrganizationMembershipCacheKey(userKey);
  if (storageKey.length > 0 && typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* ignore */
    }
  }
}


function BuildSelectedOrganizationCacheKey(userKey: string): string {
  let key = '';
  if (typeof userKey === 'string' && userKey.trim().length > 0) {
    key = `${selectedCacheKeyPrefix}${userKey.trim().toLowerCase()}`;
  }
  return key;
}

/**
 * The organization the member was last in, held so the bar can name it on the first paint
 * instead of after the session answers — the gap is short but it is a word appearing out of
 * nothing, and the session confirms or corrects it a moment later either way.
 */
export function ReadSelectedOrganizationCache(userKey: string): OrganizationSelection | null {
  let selection: OrganizationSelection | null = null;
  const storageKey = BuildSelectedOrganizationCacheKey(userKey);
  if (storageKey.length > 0 && typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = OrganizationSelections.From(JSON.parse(raw));
        if (parsed) {
          selection = parsed;
        }
      }
    } catch {
      selection = null;
    }
  }
  return selection;
}

export function WriteSelectedOrganizationCache(userKey: string, selection: OrganizationSelection): void {
  const storageKey = BuildSelectedOrganizationCacheKey(userKey);
  if (storageKey.length > 0 && typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(selection));
    } catch {
      /* quota / private mode — ignore */
    }
  }
}
